import React, { useEffect, useRef, useState } from "react";
import { FaLeaf } from "react-icons/fa";

const THUMB_HEIGHT = 32;
const clamp = (value) => Math.min(1, Math.max(0, value));

export default function ModalScrollArea({ children, id, open }) {
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const dragRef = useRef(null);
  const [position, setPosition] = useState({ progress: 0, scrollable: false });

  useEffect(() => {
    if (!open) return undefined;
    const viewport = viewportRef.current;
    const updatePosition = () => {
      const range = viewport.scrollHeight - viewport.clientHeight;
      setPosition({
        progress: range > 0 ? clamp(viewport.scrollTop / range) : 0,
        scrollable: range > 1,
      });
    };

    updatePosition();
    viewport.addEventListener("scroll", updatePosition, { passive: true });
    window.addEventListener("resize", updatePosition);
    // Both the visible area and the form can resize independently.
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updatePosition);
    observer?.observe(viewport);
    if (viewport.firstElementChild) observer?.observe(viewport.firstElementChild);
    return () => {
      viewport.removeEventListener("scroll", updatePosition);
      window.removeEventListener("resize", updatePosition);
      observer?.disconnect();
      dragRef.current = null;
    };
  }, [open]);

  const moveThumb = (clientY) => {
    const viewport = viewportRef.current;
    const track = trackRef.current.getBoundingClientRect();
    const travel = track.height - THUMB_HEIGHT;
    if (travel <= 0) return;
    const progress = clamp((clientY - track.top - dragRef.current.offset) / travel);
    viewport.scrollTop = progress * Math.max(0, viewport.scrollHeight - viewport.clientHeight);
  };

  const handlePointerDown = (event) => {
    if (!event.isPrimary || event.button !== 0) return;
    event.preventDefault();
    const bounds = event.currentTarget.getBoundingClientRect();
    dragRef.current = { pointerId: event.pointerId, offset: event.clientY - bounds.top };
    event.currentTarget.focus({ preventScroll: true });
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event) => {
    if (dragRef.current?.pointerId === event.pointerId) moveThumb(event.clientY);
  };

  const handlePointerEnd = () => {
    dragRef.current = null;
  };

  const handleKeyDown = (event) => {
    const viewport = viewportRef.current;
    const range = Math.max(0, viewport.scrollHeight - viewport.clientHeight);
    const destinations = {
      ArrowDown: viewport.scrollTop + 40,
      ArrowUp: viewport.scrollTop - 40,
      PageDown: viewport.scrollTop + viewport.clientHeight * 0.9,
      PageUp: viewport.scrollTop - viewport.clientHeight * 0.9,
      Home: 0,
      End: range,
    };
    if (Object.prototype.hasOwnProperty.call(destinations, event.key)) {
      event.preventDefault();
      viewport.scrollTop = Math.max(0, Math.min(range, destinations[event.key]));
    }
  };

  return (
    <div className="modal-scroll-frame">
      <div className="modal-scroll-area" id={id} ref={viewportRef}>
        {children}
      </div>
      <div className={`modal-scrollbar${position.scrollable ? " is-scrollable" : ""}`} ref={trackRef}>
        <div className="modal-scrollbar-track" aria-hidden="true" />
        <div
          className="modal-scrollbar-leaf"
          role="scrollbar"
          aria-label="Scroll inquiry form"
          aria-controls={id}
          aria-orientation="vertical"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(position.progress * 100)}
          tabIndex={position.scrollable ? 0 : -1}
          style={{ top: `calc(${position.progress * 100}% - ${position.progress * THUMB_HEIGHT}px)` }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerEnd}
          onPointerCancel={handlePointerEnd}
          onLostPointerCapture={handlePointerEnd}
          onKeyDown={handleKeyDown}
        >
          <FaLeaf aria-hidden="true" />
        </div>
      </div>
    </div>
  );
}
